import type { z } from 'zod';

/**
 * Raised by `formSchemaMetaSchema` when a form schema is authored. The reader
 * is an administrator in the builder, so each issue points at a field.
 */
export const FORM_SCHEMA_ISSUE_CODES = [
  'FORM_FIELD_KEY_INVALID',
  'FORM_FIELD_KEY_DUPLICATE',
  'FORM_VISIBLE_ON_SELF_REFERENCE',
  'FORM_VISIBLE_ON_UNKNOWN_FIELD',
  'FORM_VISIBLE_ON_NOT_ONE_LEVEL',
  'FORM_PATTERN_TOO_LONG',
  'FORM_PATTERN_UNCOMPILABLE',
  'FORM_DATE_INVALID',
] as const;

/**
 * Raised by the schema `buildFormDataSchema` compiles when `form_data` is
 * submitted. The reader is a donor or a clerk, and the screen renders each
 * against the field it came from, interpolating the label from the schema.
 */
export const FORM_DATA_ISSUE_CODES = [
  'FORM_FIELD_REQUIRED',
  'FORM_VALUE_TOO_SHORT',
  'FORM_VALUE_TOO_LONG',
  'FORM_VALUE_PATTERN',
  'FORM_VALUE_NOT_AN_EMAIL',
  'FORM_VALUE_NOT_A_PHONE',
  'FORM_VALUE_NOT_INTEGER',
  'FORM_VALUE_TOO_SMALL',
  'FORM_VALUE_TOO_LARGE',
  'FORM_VALUE_OFF_STEP',
  'FORM_VALUE_NOT_A_DATE',
  'FORM_VALUE_BEFORE_MIN_DATE',
  'FORM_VALUE_AFTER_MAX_DATE',
  'FORM_VALUE_MUST_BE_ACCEPTED',
  'FORM_VALUE_TOO_FEW_SELECTED',
  'FORM_VALUE_TOO_MANY_SELECTED',
  'FORM_VALUE_TOO_MANY_FILES',
  'FORM_VALUE_NOT_A_FILE',
  'FORM_VALUE_NOT_AN_OPTION',
] as const;

export const FORM_ISSUE_CODES = [...FORM_SCHEMA_ISSUE_CODES, ...FORM_DATA_ISSUE_CODES] as const;

export type FormIssueCode = (typeof FORM_ISSUE_CODES)[number];

/**
 * A Zod issue that carries no `FORM_*` code — a missing label, or a value of
 * the wrong JSON type — is reported under the generic code rather than
 * inventing a message for it.
 */
export const FORM_ISSUE_FALLBACK_CODE = 'VALIDATION_FAILED';

export type FormIssue = {
  code: FormIssueCode | typeof FORM_ISSUE_FALLBACK_CODE;
  path: (string | number)[];
};

const FORM_ISSUE_CODE_SET: ReadonlySet<string> = new Set(FORM_ISSUE_CODES);

export function isFormIssueCode(value: string): value is FormIssueCode {
  return FORM_ISSUE_CODE_SET.has(value);
}

function pathOf(path: readonly PropertyKey[]): (string | number)[] {
  return path.map((segment) => (typeof segment === 'number' ? segment : String(segment)));
}

/** Reads the stable code off each issue's message, where `form-schema.ts` puts it. */
export function formIssuesOf(error: z.ZodError): FormIssue[] {
  return error.issues.map((issue) => ({
    code: isFormIssueCode(issue.message) ? issue.message : FORM_ISSUE_FALLBACK_CODE,
    path: pathOf(issue.path),
  }));
}

/**
 * Not a `DomainError`: its code is whichever `FORM_*` code the first issue
 * carries, read off the Zod issue, and every issue travels with it so a form
 * can put each message on its own field. The API maps it to 422.
 */
export class FormRejection extends Error {
  readonly code: FormIssue['code'];
  readonly issues: FormIssue[];

  constructor(error: z.ZodError) {
    const issues = formIssuesOf(error);
    const first = issues.find((issue) => issue.code !== FORM_ISSUE_FALLBACK_CODE);
    const code = first?.code ?? FORM_ISSUE_FALLBACK_CODE;
    super(`Form rejected: ${code}`);
    this.name = 'FormRejection';
    this.code = code;
    this.issues = issues;
  }
}
